import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Native-feeling pull-to-refresh for a scroll container.
 *
 * - Activates only when the container is scrolled to the very top.
 * - Drag distance is "rubber-banded" (×0.5) so it feels resistant.
 * - When the user releases past `threshold` we fire `onRefresh()` and keep
 *   the spinner visible while the promise resolves.
 *
 * The caller receives `pullDistance` (px, 0 when idle) and `refreshing` to
 * render an indicator above the content.
 */
export function usePullToRefresh({ scrollRef, onRefresh, threshold = 80, enabled = true }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);
  const currentDistance = useRef(0);

  const reset = useCallback(() => {
    pulling.current = false;
    currentDistance.current = 0;
    setPullDistance(0);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;

    const onTouchStart = (e) => {
      if (refreshing) return;
      if (el.scrollTop > 0) return;
      startY.current = e.touches[0].clientY;
      pulling.current = true;
      currentDistance.current = 0;
    };

    const onTouchMove = (e) => {
      if (!pulling.current || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && el.scrollTop === 0) {
        // Rubber-band the distance so the pull never feels weightless.
        const d = Math.min(dy * 0.5, threshold + 40);
        currentDistance.current = d;
        setPullDistance(d);
      } else if (dy <= 0) {
        currentDistance.current = 0;
        setPullDistance(0);
      }
    };

    const onTouchEnd = async () => {
      if (!pulling.current) return;
      const d = currentDistance.current;
      pulling.current = false;
      if (d >= threshold && !refreshing) {
        setRefreshing(true);
        setPullDistance(threshold);
        try {
          await onRefresh?.();
        } finally {
          setRefreshing(false);
          setPullDistance(0);
          currentDistance.current = 0;
        }
      } else {
        setPullDistance(0);
        currentDistance.current = 0;
      }
    };

    const onTouchCancel = () => {
      reset();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchCancel);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [scrollRef, onRefresh, threshold, enabled, refreshing, reset]);

  return { pullDistance, refreshing };
}
